import { StrictMode, useState, useEffect, useRef, useCallback } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useParams,
} from "react-router";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import * as Sentry from "@sentry/react";
import { Html5Qrcode, Html5QrcodeScannerState } from "html5-qrcode";
import { QrCode, X, Sparkles, ChevronDown, ChevronUp } from "lucide-react";
import { EventPage } from "@/routes/EventPage";
import "@/lib/i18n"; // Initialize i18n before rendering
import "@/styles/globals.css";

// Initialize Sentry for error tracking
if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.PROD ? "production" : "development",
    integrations: [Sentry.browserTracingIntegration()],
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1.0,
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
    },
  },
});

function JoinPage() {
  const navigate = useNavigate();
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [showWhatsNew, setShowWhatsNew] = useState(false);
  const scannerRef = useRef<Html5Qrcode | null>(null);
  const scannerContainerId = "qr-scanner";

  const resolveCode = useCallback(
    async (joinCode: string) => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetch(`/api/join/${joinCode}`);
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as {
            error?: string;
          } | null;
          setError(
            data?.error || "Event not found. Check the code and try again.",
          );
          return;
        }
        const { slug } = (await res.json()) as { slug: string };
        navigate(`/e/${slug}`);
      } catch {
        setError("Network error. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [navigate],
  );

  const handleQrResult = useCallback(
    (decodedText: string) => {
      if (scannerRef.current) {
        try {
          if (
            scannerRef.current.getState() === Html5QrcodeScannerState.SCANNING
          ) {
            scannerRef.current.stop().catch(console.error);
          }
        } catch {
          // Ignore
        }
      }
      setScanning(false);

      try {
        const url = new URL(decodedText);
        const joinParam = url.searchParams.get("join");

        if (joinParam) {
          const trimmed = joinParam.trim().toLowerCase();
          if (/^[a-z]{4}$/.test(trimmed)) {
            resolveCode(trimmed);
            return;
          }
        }

        const pathMatch = url.pathname.match(/^\/e\/([^/?]+)/);
        if (pathMatch) {
          navigate(`/e/${pathMatch[1]}`);
          return;
        }

        const joinMatch = url.pathname.match(/^\/join\/([a-z]{4})$/i);
        if (joinMatch) {
          resolveCode(joinMatch[1].toLowerCase());
          return;
        }
      } catch {
        // Not a URL
      }

      const trimmed = decodedText.trim().toLowerCase();
      if (/^[a-z]{4}$/.test(trimmed)) {
        resolveCode(trimmed);
        return;
      }

      setError("Unrecognized QR code. Try entering the code manually.");
    },
    [navigate, resolveCode],
  );

  const startScanner = useCallback(async () => {
    setError(null);
    setScanning(true);

    await new Promise((r) => setTimeout(r, 100));

    try {
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;

      await scanner.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        (decodedText) => handleQrResult(decodedText),
        () => {},
      );
    } catch {
      setError("Unable to access camera for scanning.");
      setScanning(false);
    }
  }, [handleQrResult]);

  const stopScanner = useCallback(() => {
    if (scannerRef.current) {
      try {
        if (
          scannerRef.current.getState() === Html5QrcodeScannerState.SCANNING
        ) {
          scannerRef.current.stop().catch(console.error);
        }
      } catch {
        // Ignore
      }
      scannerRef.current = null;
    }
    setScanning(false);
  }, []);

  useEffect(() => {
    return () => {
      if (scannerRef.current) {
        try {
          if (
            scannerRef.current.getState() === Html5QrcodeScannerState.SCANNING
          ) {
            scannerRef.current.stop().catch(console.error);
          }
        } catch {
          // Ignore
        }
      }
    };
  }, []);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = code.trim().toLowerCase();
    if (!/^[a-z]{4}$/.test(trimmed)) {
      setError("Enter a 4-letter code");
      return;
    }
    resolveCode(trimmed);
  }

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center p-6 text-center">
      <img
        src="/vizi-logo.png"
        className="mb-4 h-24 w-24 rounded-2xl"
        alt="Vizi Fan Mix"
      />
      <h1 className="mb-2 text-2xl font-bold text-vizimix-100">
        Vizi Fan Mix
      </h1>

      {/* QR Scanner section */}
      {scanning ? (
        <div className="mb-6 w-full max-w-xs">
          <p className="mb-3 text-vizimix-300">
            Point your camera at the QR code
          </p>
          <div className="relative overflow-hidden rounded-2xl border border-vizimix-700 bg-black">
            <div id={scannerContainerId} className="w-full" />
            <button
              onClick={stopScanner}
              className="absolute right-2 top-2 z-10 rounded-full bg-black/60 p-2 text-white backdrop-blur-sm transition-colors hover:bg-black/80"
              aria-label="Close scanner"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      ) : (
        <div className="mb-6 flex flex-col items-center">
          <p className="mb-4 text-vizimix-300">Scan an event QR code</p>
          <button
            onClick={startScanner}
            className="flex h-24 w-24 items-center justify-center rounded-2xl border-2 border-vizimix-600 bg-vizimix-900/50 text-vizimix-400 transition-colors hover:border-vizimix-500 hover:bg-vizimix-800/50 hover:text-vizimix-300"
            aria-label="Scan QR code"
          >
            <QrCode className="h-12 w-12" />
          </button>
        </div>
      )}

      {/* Separator */}
      <div className="mb-6 flex w-full max-w-xs items-center gap-3">
        <div className="h-px flex-1 bg-vizimix-700" />
        <span className="text-sm text-vizimix-400">
          or enter the code below
        </span>
        <div className="h-px flex-1 bg-vizimix-700" />
      </div>

      {/* Code entry form */}
      <form
        onSubmit={handleSubmit}
        className="flex w-full max-w-xs flex-col items-center gap-3"
      >
        <input
          type="text"
          value={code}
          onChange={(e) => {
            const val = e.target.value
              .toLowerCase()
              .replace(/[^a-z]/g, "")
              .slice(0, 4);
            setCode(val);
            setError(null);
          }}
          placeholder="abcd"
          maxLength={4}
          autoComplete="off"
          autoCapitalize="off"
          className="w-full rounded-lg border border-vizimix-700 bg-vizimix-900/50 px-4 py-3 text-center font-mono text-3xl font-bold uppercase tracking-widest text-vizimix-100 placeholder:text-vizimix-600 focus:outline-none focus:ring-2 focus:ring-vizimix-500"
        />
        <button
          type="submit"
          disabled={code.length !== 4 || loading}
          className="w-full rounded-lg bg-vizimix-500 px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-vizimix-400 disabled:cursor-not-allowed disabled:opacity-40"
        >
          {loading ? "Joining..." : "Join Event"}
        </button>
        {error && <p className="text-sm text-red-400">{error}</p>}
      </form>

      {/* Version / What's New */}
      <div className="mt-8 w-full max-w-xs">
        <button
          onClick={() => setShowWhatsNew(!showWhatsNew)}
          className="flex w-full items-center justify-center gap-1.5 text-xs text-vizimix-500 hover:text-vizimix-300 transition-colors py-2"
        >
          <Sparkles className="h-3 w-3" />
          <span>v{__APP_VERSION__}</span>
          {showWhatsNew ? (
            <ChevronUp className="h-3 w-3" />
          ) : (
            <ChevronDown className="h-3 w-3" />
          )}
        </button>
        {showWhatsNew && (
          <div className="rounded-lg border border-vizimix-700 bg-vizimix-900/50 p-3 space-y-1.5">
            {__RECENT_CHANGES__.length > 0 ? (
              [...__RECENT_CHANGES__]
                .sort((a, b) => {
                  const order = { fix: 0, feat: 1, perf: 2 };
                  return order[a.type] - order[b.type];
                })
                .slice(0, 10)
                .map((change) => (
                  <div key={change.hash} className="flex items-start gap-1.5 text-xs">
                    <span
                      className={[
                        "text-[9px] font-medium px-1 py-0.5 rounded-full shrink-0 mt-px",
                        change.type === "fix" ? "bg-green-500/20 text-green-400" :
                        change.type === "feat" ? "bg-blue-500/20 text-blue-400" :
                        "bg-orange-500/20 text-orange-400",
                      ].join(" ")}
                    >
                      {change.type === "feat" ? "New" : change.type === "fix" ? "Fix" : "Perf"}
                    </span>
                    <span className="text-vizimix-300 flex-1 min-w-0 break-words leading-tight">
                      {change.message}
                    </span>
                  </div>
                ))
            ) : (
              <p className="text-xs text-vizimix-500">No recent changes</p>
            )}
            <p className="text-[10px] text-vizimix-500 text-center pt-1">
              Built {new Date(__BUILD_TIME__).toLocaleString()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function JoinByCode() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!code) {
      navigate("/", { replace: true });
      return;
    }
    const trimmed = code.trim().toLowerCase();
    if (!/^[a-z]{4}$/.test(trimmed)) {
      navigate("/", { replace: true });
      return;
    }
    fetch(`/api/join/${trimmed}`)
      .then(async (res) => {
        if (!res.ok) {
          const data = (await res.json().catch(() => null)) as {
            error?: string;
          } | null;
          setError(
            data?.error || "Event not found. Check the code and try again.",
          );
          return;
        }
        const { slug } = (await res.json()) as { slug: string };
        navigate(`/e/${slug}`, { replace: true });
      })
      .catch(() => setError("Network error"));
  }, [code, navigate]);

  if (error) {
    return (
      <div className="flex min-h-dvh flex-col items-center justify-center p-6 text-center">
        <p className="mb-4 text-red-400">{error}</p>
        <button
          onClick={() => navigate("/")}
          className="text-sm text-vizimix-400 underline hover:text-vizimix-200"
        >
          Try another code
        </button>
      </div>
    );
  }

  return (
    <div className="flex min-h-dvh items-center justify-center">
      <div className="h-8 w-8 animate-spin rounded-full border-2 border-vizimix-500 border-t-transparent" />
    </div>
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          <Route path="/e/:slug" element={<EventPage />} />
          <Route path="/join/:code" element={<JoinByCode />} />
          <Route path="/" element={<JoinPage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </QueryClientProvider>
  </StrictMode>,
);
